import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SizeError, MarginInstrument } from '@swyftx/margin/build/types';
import { getSizeError } from '@swyftx/margin/build/utils';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginInstrumentDetails } from '../instruments';

type Props = {
  lots: string;
  market?: MarginInstrument;
  maxMarginPosition: Big;
};

const useMarginLotsError = ({ lots, market, maxMarginPosition }: Props) => {
  const { t } = useTranslation('margin');
  const { secondaryAsset } = useMarginInstrumentDetails({ instrument: market });

  const error = useMemo(() => {
    if (!market || !lots) return undefined;

    const minOrderIncrement = Big(10).pow(-market.precision).toNumber();
    const minOrderSize = Number(market.minLots);
    const maxPositionSize = Number(market.maxLots);
    const maxMarginRequirement = maxMarginPosition.toNumber();

    const sizeError = getSizeError(minOrderIncrement, minOrderSize, maxMarginRequirement, maxPositionSize, lots);

    if (sizeError === undefined) return undefined;

    switch (sizeError) {
      case SizeError.IncrementTooSmall:
        return t('position.errors.incrementTooSmall', { incrementSize: market.minLotIncrement });
      case SizeError.OrderAboveMax:
        return t('position.errors.orderAboveMax', {
          maxPositionSize: formatCurrency(maxPositionSize, secondaryAsset, { hideCode: false, appendCode: true }),
        });
      case SizeError.OrderAboveUserMax:
        return 'Size exceeds maximum based on available margin.';
      case SizeError.OrderBelowMin:
        return t('position.errors.orderBelowMin', {
          minOrderSize: formatCurrency(minOrderSize, secondaryAsset, { hideCode: false, appendCode: true }),
        });
      default:
        sizeError satisfies never;
    }
  }, [market, lots, maxMarginPosition, t, secondaryAsset]);

  return {
    error,
  };
};

export { useMarginLotsError };
